import React from 'react'
import { Card } from '@components/common/card'
import { Separator } from '@components/common/separator'
import { Badge } from '@components/common/badge'
import { cn } from '@pma/utils/helpers'
import { DictType } from '@pma/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth } from '@stores/auth-store'
import { parseCookies } from 'nookies'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import axios from 'axios'
import { SERVER_URL, USER_STATUS_DICTIONARY_ENDPOINT } from '@pma/utils/api-routes'
import AddStatus from './add-status-modal'
import { X } from 'lucide-react'
import { Button } from '@components/common/button'
import { toast } from '@hooks/use-toast'

import PmsLoader from '@components/common/loader'
import useTranslator from '@hooks/use-translator'

const UserStatusOptions = () => {
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const { translate } = useTranslator()
  const { authConfig } = useAuth()

  const userId = authConfig?.id
  const orgId = authConfig?.last_organization_id
  const getUserStatus = async () => {
    const response = await axios.get(
      SERVER_URL +
        USER_STATUS_DICTIONARY_ENDPOINT +
        `?context_type=organization%20user&context_id=${orgId}&for_task=true`,
      {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const deleteStatus = async (id: number) => {
    const response = await axios.delete(
      SERVER_URL + USER_STATUS_DICTIONARY_ENDPOINT + `/delete/${id}`,
      {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response?.data
  }

  const deleteStatusMutation = useMutation({
    mutationKey: ['delete-status'],
    mutationFn: (id: number) => deleteStatus(id),
    onSuccess: () => {
      refetch()
      toast({
        title: translate('Success'),
        description: translate('User status updated'),
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: translate('Error'),
        description: translate('Unable to delete status'),
        variant: 'error',
      })
    },
  })

  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: ['user-status'],
    queryFn: getUserStatus,
  })

  return (
    <Card className='min-h-[200px] p-3'>
      <div className='flex items-center justify-between'>
        <h3 className='tex-lg font-semibold'>{translate('Users Status')}</h3>

        <AddStatus option='user' revalidate={refetch} />
      </div>

      <Separator className='my-2' />
      {isLoading && <PmsLoader />}
      <div className='mt-4 flex flex-wrap gap-3'>
        {data?.map((status: DictType) => (
          <Badge className={cn('relative')} variant={'premium'} key={status?.id}>
            {translate(status?.name)}
            <Button
              variant={'outline'}
              size={'icon'}
              className='absolute -right-2 -top-1 h-3 w-3 rounded-full text-red-500 hover:scale-125'
              onClick={() => deleteStatusMutation.mutate(status?.id)}
            >
              <X className='rounded-md' />
            </Button>
          </Badge>
        ))}
      </div>
    </Card>
  )
}

export default UserStatusOptions
